import {Auth} from 'aws-amplify'
import {CognitoUser} from 'amazon-cognito-identity-js'
import React, {useEffect, useState} from 'react'
import {Modal} from 'react-bootstrap'
import {toast} from 'react-toastify'
import {toastDefaultConfig} from '../../../shared/config/toastConfig'
import {getLogger} from '../../../shared/utils/logUtils'
import {useCognitoAuth} from '../auth/core/CognitoAuth'
import {
  disableCognitoSmsMfa,
  enableCognitoSmsMfa,
  resolveSmsMfaEnabled,
} from '../auth/core/resolveSmsMfaStatus'

const E164_REGEX = /^\+[1-9]\d{6,14}$/

/**
 * Federated Google sign-in uses a Cognito username like `Google_<sub>`, not the email alias.
 * The `identities` attribute is *not* reliable: it can list Google for linked accounts that still
 * sign in with email + password + SMS.
 */
function isGoogleFederatedPrimaryUsername(user: CognitoUser): boolean {
  const username = user.getUsername?.() ?? ''
  return /^google_/i.test(username)
}

export type MfaSmsSettingsModalProps = {
  show: boolean
  onHide: () => void
  onStatusChanged: () => void
}

export const MfaSmsSettingsModal: React.FC<MfaSmsSettingsModalProps> = ({
  show,
  onHide,
  onStatusChanged,
}) => {
  const {cognitoAuth} = useCognitoAuth()
  const [loading, setLoading] = useState(false)
  const [mfaEnabled, setMfaEnabled] = useState(false)
  const [isGoogle, setIsGoogle] = useState(false)
  const [step, setStep] = useState<'choose' | 'phone' | 'code'>('choose')
  const [phoneE164, setPhoneE164] = useState('')
  const [verificationCode, setVerificationCode] = useState('')

  useEffect(() => {
    if (!show || !cognitoAuth) {
      return
    }
    let cancelled = false
    const load = async () => {
      try {
        if (!cancelled) {
          setIsGoogle(isGoogleFederatedPrimaryUsername(cognitoAuth))
        }
        const smsOn = await resolveSmsMfaEnabled(cognitoAuth)
        if (!cancelled) {
          setMfaEnabled(smsOn)
        }
      } catch (e) {
        getLogger().error(e)
      }
    }
    void load()
    return () => {
      cancelled = true
    }
  }, [show, cognitoAuth])

  useEffect(() => {
    if (!show) {
      setStep('choose')
      setPhoneE164('')
      setVerificationCode('')
    }
  }, [show])

  const handleDisable = async () => {
    if (!cognitoAuth) {
      return
    }
    setLoading(true)
    try {
      const updated = await disableCognitoSmsMfa(cognitoAuth)
      if (updated) {
        toast('SMS two-factor authentication is turned off.', {...toastDefaultConfig, type: 'info'})
      } else {
        toast('SMS two-factor was already off for this account.', {...toastDefaultConfig, type: 'info'})
      }
      setMfaEnabled(false)
      onStatusChanged()
      onHide()
    } catch (e: unknown) {
      toast(e instanceof Error ? e.message : 'Could not disable MFA', {
        ...toastDefaultConfig,
        type: 'error',
      })
      getLogger().error(e)
    } finally {
      setLoading(false)
    }
  }

  const handleSendPhone = async () => {
    if (!cognitoAuth || !E164_REGEX.test(phoneE164.trim())) {
      toast('Enter a valid phone number in international format (e.g. +436601234567).', {
        ...toastDefaultConfig,
        type: 'error',
      })
      return
    }
    setLoading(true)
    try {
      await Auth.updateUserAttributes(cognitoAuth, {
        phone_number: phoneE164.trim(),
      })
      await Auth.verifyUserAttribute(cognitoAuth, 'phone_number')
      setStep('code')
      toast('We sent a code to your phone.', {...toastDefaultConfig, type: 'info'})
    } catch (e: unknown) {
      toast(e instanceof Error ? e.message : 'Could not start phone verification', {
        ...toastDefaultConfig,
        type: 'error',
      })
      getLogger().error(e)
    } finally {
      setLoading(false)
    }
  }

  const handleConfirmCode = async () => {
    if (!cognitoAuth || verificationCode.trim().length < 4) {
      return
    }
    setLoading(true)
    try {
      await Auth.verifyUserAttributeSubmit(cognitoAuth, 'phone_number', verificationCode.trim())
      await enableCognitoSmsMfa(cognitoAuth)
      toast('SMS two-factor authentication is enabled.', {...toastDefaultConfig, type: 'success'})
      setMfaEnabled(true)
      onStatusChanged()
      onHide()
    } catch (e: unknown) {
      toast(e instanceof Error ? e.message : 'Invalid code', {...toastDefaultConfig, type: 'error'})
      getLogger().error(e)
    } finally {
      setLoading(false)
    }
  }

  return (
    <Modal show={show} onHide={onHide} centered dialogClassName='ob-dialog'>
      <Modal.Header closeButton>
        <Modal.Title>SMS two-factor authentication</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        {!cognitoAuth && <p className='text-gray-600'>Sign in to manage two-factor authentication.</p>}
        {cognitoAuth && isGoogle && (
          <p className='text-gray-600'>
            This account signs in with Google only. Google handles authentication for that path, so
            Cognito SMS 2FA for email and password does not apply here.
          </p>
        )}
        {cognitoAuth && !isGoogle && mfaEnabled && step === 'choose' && (
          <div>
            <p className='mb-4'>SMS verification is currently enabled for your account.</p>
            <button
              type='button'
              className='btn btn-light-danger'
              disabled={loading}
              onClick={() => void handleDisable()}
            >
              Turn off SMS two-factor
            </button>
          </div>
        )}
        {cognitoAuth && !isGoogle && !mfaEnabled && step === 'choose' && (
          <div>
            <p className='text-gray-600 mb-4'>
              After you enable this, you will enter a code sent by SMS each time you sign in with
              email and password.
            </p>
            <button
              type='button'
              className='btn btn-primary'
              disabled={loading}
              onClick={() => setStep('phone')}
            >
              Enable SMS two-factor
            </button>
          </div>
        )}
        {cognitoAuth && !isGoogle && step === 'phone' && (
          <div>
            <label className='form-label fw-bold' htmlFor='mfa-phone-e164'>
              Mobile number (E.164)
            </label>
            <input
              id='mfa-phone-e164'
              className='form-control form-control-solid mb-3'
              placeholder='+436601234567'
              value={phoneE164}
              onChange={(e) => setPhoneE164(e.target.value)}
              autoComplete='tel'
            />
            <div className='d-flex gap-2'>
              <button
                type='button'
                className='btn btn-light'
                disabled={loading}
                onClick={() => setStep('choose')}
              >
                Back
              </button>
              <button
                type='button'
                className='btn btn-primary'
                disabled={loading}
                onClick={() => void handleSendPhone()}
              >
                Send verification code
              </button>
            </div>
          </div>
        )}
        {cognitoAuth && !isGoogle && step === 'code' && (
          <div>
            <label className='form-label fw-bold' htmlFor='mfa-phone-code'>
              Verification code
            </label>
            <input
              id='mfa-phone-code'
              className='form-control form-control-solid mb-3'
              inputMode='numeric'
              autoComplete='one-time-code'
              value={verificationCode}
              onChange={(e) => setVerificationCode(e.target.value.replace(/\D/g, '').slice(0, 8))}
            />
            <div className='d-flex gap-2'>
              <button
                type='button'
                className='btn btn-light'
                disabled={loading}
                onClick={() => setStep('phone')}
              >
                Back
              </button>
              <button
                type='button'
                className='btn btn-primary'
                disabled={loading || verificationCode.trim().length < 4}
                onClick={() => void handleConfirmCode()}
              >
                Verify and enable
              </button>
            </div>
          </div>
        )}
      </Modal.Body>
    </Modal>
  )
}
